import {RefObject, useEffect} from 'react';

// Trackpads send a gesture (and its momentum) as a stream of wheel events; a pause this long ends it.
const GESTURE_IDLE_MS = 120;
const LINE_HEIGHT_PX = 16;

/**
 * Locks each wheel/trackpad gesture over a horizontal scroller to the axis it starts on, so a
 * sideways swipe never drifts the page vertically (and a vertical one passes straight through).
 */
export const useWheelAxisLock = (ref: RefObject<HTMLElement | null>) => {
  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    let axis: 'x' | 'y' | null = null;
    let idleTimer: number | undefined;

    const handleWheel = (event: WheelEvent) => {
      // Pinch-to-zoom arrives as ctrl+wheel; leave it to the browser.
      if (event.ctrlKey) return;

      window.clearTimeout(idleTimer);
      idleTimer = window.setTimeout(() => {
        axis = null;
      }, GESTURE_IDLE_MS);

      if (axis === null) {
        if (event.deltaX === 0 && event.deltaY === 0) return;
        axis = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? 'x' : 'y';
      }
      if (axis !== 'x') return;

      event.preventDefault();
      const scale = event.deltaMode === WheelEvent.DOM_DELTA_LINE ? LINE_HEIGHT_PX : 1;
      element.scrollLeft += event.deltaX * scale;
    };

    element.addEventListener('wheel', handleWheel, {passive: false});
    return () => {
      window.clearTimeout(idleTimer);
      element.removeEventListener('wheel', handleWheel);
    };
  }, [ref]);
};
